import React from "react";
import DataTable from "react-data-table-component";
import { AssetTypeToHumanString } from "../../../components/Asset/AssetTypeHumanLabel.tsx";
import BlockSpinner from "../../../components/Spinners/BlockSpinner.tsx";
import CopyToClipboard from "../../../components/utils/CopyToClipboard.tsx";
import type { AssetRegisterItem } from "../../../kubb";

interface AssetRegisterSelectComponentProps {
  items: AssetRegisterItem[];
  loading: boolean;
  setValues: (values: AssetRegisterItem[]) => void;
}

export default function AssetRegisterSelectComponent(
  props: AssetRegisterSelectComponentProps,
): React.ReactElement {
  const onSelect = (selected: {
    allSelected: boolean;
    selectedCount: number;
    selectedRows: AssetRegisterItem[];
  }) => {
    props.setValues(selected.selectedRows);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: want to trigger when items change
  React.useEffect(() => {
    props.setValues([]);
  }, [props.items, props.setValues]);

  const BlankTable = (
    <BlockSpinner loading={true}>
      <div style={{ height: "300px" }}>
        <DataTable
          columns={[
            {
              name: "Address",
              selector: () => "...",
              wrap: true,
            },

            {
              name: "Group",
              selector: () => "...",
              wrap: true,
            },
            {
              name: "Location Ref",
              selector: () => "...",
              wrap: true,
            },
            {
              name: "Asset Position",
              selector: () => "...",
              wrap: true,
            },
            {
              name: "Asset Type",
              selector: () => "...",
              wrap: true,
            },
            {
              name: "Current Asset",
              selector: () => "...",
              wrap: true,
            },
          ]}
          data={[{}, {}, {}]}
          selectableRows={true}
          dense={true}
          fixedHeader={true}
          fixedHeaderScrollHeight={"300px"}
        />
      </div>
    </BlockSpinner>
  );

  return (
    <>
      <DataTable
        progressPending={props.loading}
        progressComponent={BlankTable}
        columns={[
          {
            name: "Address",
            selector: (row: AssetRegisterItem) => row.location.address,
            wrap: true,
          },

          {
            name: "Group",
            selector: (row: AssetRegisterItem) => row.location.group || "",
            wrap: true,
          },
          {
            name: "Location Ref",
            selector: (row: AssetRegisterItem) =>
              row.location.location_reference || row.location.location_id,
            cell: (row: AssetRegisterItem) =>
              row.location.location_reference ? (
                <>{row.location.location_reference}</>
              ) : (
                <CopyToClipboard value={row.location.location_id} slice={8} />
              ),

            wrap: true,
          },
          {
            name: "Asset Position",
            selector: (row: AssetRegisterItem) =>
              row.asset_position.asset_position_reference ||
              `...${row.asset_position.asset_position_id.slice(0, 8)}`,
            cell: (row: AssetRegisterItem) =>
              row.asset_position.asset_position_reference ? (
                <>{row.asset_position.asset_position_reference}</>
              ) : (
                <CopyToClipboard
                  value={row.asset_position.asset_position_id}
                  slice={8}
                />
              ),
            wrap: true,
          },
          {
            name: "Asset Type",
            selector: (row: AssetRegisterItem) =>
              AssetTypeToHumanString(row.asset_position.asset_position_type),
            wrap: true,
          },
          {
            name: "Current Asset",
            selector: (row: AssetRegisterItem) =>
              row.asset?.serial_number || "",
            wrap: true,
          },
        ]}
        data={props.items}
        selectableRows={true}
        onSelectedRowsChange={onSelect}
        dense={true}
        fixedHeader={true}
        fixedHeaderScrollHeight={"300px"}
      />
    </>
  );
}
